import { z } from "zod";

// Shared zod fragments used across every form in the app. Keep the rules
// consistent (one password min-length, one email regex, one AU postcode
// shape) so that tightening or relaxing a validation is a one-line change,
// not a hunt across files.
//
// Error messages follow the brand voice: warm, direct, no em-dashes, no
// exclamation marks. Prefer second-person phrasing where it reads well.

// Matches the same shape as the edge-function-side EMAIL_RE in
// supabase/functions/_shared/pricing.ts. Keep these in sync so that the
// client accepts exactly what the server accepts.
export const emailSchema = z
  .string({ required_error: "Please enter your email address." })
  .trim()
  .min(1, "Please enter your email address.")
  // MS163: RFC 5321 max + matches the platform-wide cap convention
  // (admin-create-company, MS155/MS156/MS157 DB caps, MS158-162
  // client maxLength on form inputs). Pre-MS163 the schema had no
  // max — a 1MB string with one `@` and one `.` would pass
  // validation and propagate downstream.
  .max(320, "Email is too long (max 320 characters).")
  .email("That doesn't look like a valid email address.");

// Email that is OK to leave empty, but must be well-formed if provided.
// Use this for fields like "know a colleague who'd love this?" where the
// user can skip entirely.
export const optionalEmailSchema = z
  .string()
  .trim()
  // MS163: same 320 max as emailSchema. Defensive against the same
  // unbounded-input gap.
  .max(320, "Email is too long (max 320 characters).")
  .refine(
    (v) => v === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
    "That doesn't look like a valid email address.",
  );

// Password rule: minimum 8 characters + at least one letter + one number.
// Tightest existing rule we had, applied consistently everywhere. Does not
// enforce special characters; we want a usable minimum, not a theatre rule.
export const passwordSchema = z
  .string({ required_error: "Give your password at least 8 characters." })
  .min(8, "Give your password at least 8 characters.")
  .regex(/[A-Za-z]/, "Include at least one letter.")
  .regex(/[0-9]/, "Include at least one number.");

// For forms that need a non-empty single-line input (names, subjects, etc.)
// parameterised so callers can set their own cap. Trims whitespace before
// applying min/max.
export const trimmedStringSchema = (
  opts: { min?: number; max?: number; label?: string } = {},
) => {
  const { min = 1, max = 200, label = "this field" } = opts;
  return z
    .string({ required_error: `Please fill in ${label}.` })
    .trim()
    .min(min, min === 1 ? `Please fill in ${label}.` : `Keep ${label} to at least ${min} characters.`)
    .max(max, `Keep ${label} to ${max} characters or fewer.`);
};

// Australian phone numbers, loose shape on purpose. Accepts +61, 0-prefixed
// mobile/landline, spaces, dashes, parens. Eight to fifteen digits after
// stripping non-digits covers real-world inputs without rejecting valid
// numbers.
export const auPhoneSchema = z
  .string()
  .trim()
  .refine(
    (v) => v === "" || /^[\d\s+()-]+$/.test(v),
    "Use digits, spaces, +, ( or - only.",
  )
  .refine((v) => {
    if (v === "") return true;
    const digits = v.replace(/\D/g, "");
    return digits.length >= 8 && digits.length <= 15;
  }, "That phone number doesn't look right.");

// Australian postcode: exactly 4 digits. Rejects leading zeros on the
// digit count so that "0200" (ACT) and "5000" both pass.
export const auPostcodeSchema = z
  .string()
  .trim()
  .regex(/^\d{4}$/, "Australian postcodes are 4 digits.");

// Full name, one-line helper that pre-bakes the brand-voice error.
export const fullNameSchema = z
  .string({ required_error: "Please tell us your name." })
  .trim()
  .min(1, "Please tell us your name.")
  .max(100, "Keep your name to 100 characters or fewer.");
